<div
  class="tcdl-editor"
  [class.tcdl-editor--focused]="focused()"
  [class.tcdl-editor--compact]="size() === 'compact'"
  data-testid="tcdl-editor"
>
  <div class="tcdl-editor__gutter" aria-hidden="true">
    @for (line of lines(); track line.index) {
      <div
        class="tcdl-editor__gutter-line"
        [class.tcdl-editor__gutter-line--current]="isCurrentLine(line.index)"
        [attr.data-testid]="'tcdl-gutter-line-' + line.index"
      >
        @if (line.isManual) {
          <span
            class="tcdl-editor__manual-marker"
            [attr.title]="'testSteps.form.tcdlManualTitle' | translate"
            [attr.data-testid]="'tcdl-manual-marker-' + line.index"
          >
            {{ 'testSteps.form.tcdlManualMarker' | translate }}
          </span>
        } @else {
          <span class="tcdl-editor__gutter-number">{{ line.index + 1 }}</span>
        }
      </div>
    }
  </div>

  <div class="tcdl-editor__text-col">
    <div class="tcdl-editor__overlay" aria-hidden="true">
      <div class="tcdl-editor__overlay-content" [style.transform]="overlayOffset()">
        <div
          class="tcdl-editor__caret-mirror"
          [style.top]="'calc(var(--tcdl-pad-y) + ' + caretLine() + ' * var(--tcdl-line-height))'"
        >
          <span [textContent]="caretColumnText()"></span>
          <span #caretAnchor class="tcdl-editor__caret-anchor"></span>
        </div>
        @for (line of renderedLines(); track line.index) {
          <div class="tcdl-editor__overlay-line" [class.tcdl-editor__overlay-line--manual]="line.isManual">
            @for (token of line.parts; track $index) {
              <span
                [class]="'tcdl-tok tcdl-tok--' + token.kind"
                [class.tcdl-tok--unknown]="token.status === 'unknown'"
                [class.tcdl-tok--not-implemented]="token.status === 'not-implemented'"
                [class.tcdl-tok--unknown-object]="token.unknownObject"
                [textContent]="token.text"
              ></span>
            }
          </div>
        }
      </div>
    </div>

    <textarea
      #ta
      class="tcdl-editor__textarea"
      data-testid="tcdl-editor-textarea"
      [attr.aria-label]="resolvedAriaLabel()"
      [attr.aria-describedby]="diagnostic() ? hintId + ' ' + diagnosticId : hintId"
      aria-autocomplete="list"
      [attr.aria-controls]="suggestions().length ? listboxId : null"
      [attr.aria-activedescendant]="activeDescendant()"
      [attr.maxlength]="maxLength()"
      [placeholder]="resolvedPlaceholder()"
      [value]="value()"
      [readOnly]="disabled()"
      (input)="onInput($event)"
      (keyup)="onCaretChange($event)"
      (click)="onCaretChange($event)"
      (keydown)="onKeyDown($event)"
      (scroll)="onScroll($event)"
      (focus)="onFocus()"
      (blur)="onBlur($event)"
      spellcheck="false"
    ></textarea>
  </div>
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="caretAnchor"
  [cdkConnectedOverlayOpen]="suggestionsOpen()"
  [cdkConnectedOverlayPositions]="suggestionPositions"
  cdkConnectedOverlayPanelClass="tcdl-editor__suggestions-panel"
>
  <div class="tcdl-editor__suggestions" data-testid="tcdl-suggestions">
    @if (suggestionsLoading() && !suggestions().length) {
      <div class="tcdl-editor__suggestions-loading" role="status" data-testid="tcdl-suggestions-loading">
        {{ 'testSteps.form.tcdlSuggestionsLoading' | translate }}
      </div>
    } @else {
      <ul
        class="tcdl-editor__suggestions-list"
        role="listbox"
        [id]="listboxId"
        [attr.aria-label]="'testSteps.form.tcdlSuggestionsLabel' | translate"
      >
        @for (name of suggestions(); track name; let i = $index) {
          <li role="presentation">
            <button
              type="button"
              tabindex="-1"
              class="tcdl-editor__suggestion"
              role="option"
              [id]="optionId(i)"
              [class.tcdl-editor__suggestion--active]="i === activeSuggestion()"
              [attr.aria-selected]="i === activeSuggestion()"
              (mousedown)="$event.preventDefault()"
              (click)="pickSuggestion(i)"
            >
              {{ name }}
            </button>
          </li>
        }
      </ul>
    }
  </div>
</ng-template>

<span class="tcdl-editor__live" aria-live="polite">{{ liveAnnouncement() }}</span>

<div [id]="diagnosticId" class="tcdl-editor__diagnostic" data-testid="tcdl-diagnostic" aria-live="polite">
  @if (diagnostic()) {
    <span class="tcdl-editor__diagnostic-icon" aria-hidden="true">⚠</span>
    <span data-testid="tcdl-diagnostic-text">{{ diagnostic() }}</span>
  }
</div>

<div class="tcdl-editor__footer">
  <div [id]="hintId" class="tcdl-editor__hint">
    <strong>{{ 'testSteps.form.tcdlLabel' | translate }}</strong>
    — {{ 'testSteps.form.tcdlHintSuffix' | translate }}
  </div>
  <div class="tcdl-editor__stats" data-testid="tcdl-stats">
    <span>{{ 'testSteps.form.tcdlInstructionsCount' | translate: { count: instructionCount() } }}</span>
    @if (manualCount() > 0) {
      <span class="tcdl-editor__stats-manual">
        {{ 'testSteps.form.tcdlManualCount' | translate: { count: manualCount() } }}
      </span>
    }
    @if (unknownCount() > 0) {
      <span class="tcdl-editor__stats-unknown" data-testid="tcdl-stats-unknown">
        {{ 'testSteps.form.tcdlUnknownCount' | translate: { count: unknownCount() } }}
      </span>
    }
    @if (notImplementedCount() > 0) {
      <span class="tcdl-editor__stats-not-implemented" data-testid="tcdl-stats-not-implemented">
        {{ 'testSteps.form.tcdlNotImplementedCount' | translate: { count: notImplementedCount() } }}
      </span>
    }
    @if (unknownObjectCount() > 0) {
      <span class="tcdl-editor__stats-unknown-objects" data-testid="tcdl-stats-unknown-objects">
        {{ 'tcdlEditor.stats.unknownObjects' | translate: { count: unknownObjectCount() } }}
      </span>
    }
    @if (objectCheckFailed()) {
      <span class="tcdl-editor__stats-object-check-error" data-testid="tcdl-stats-object-check-error">
        {{ 'tcdlEditor.stats.objectCheckError' | translate }}
      </span>
    }
    @if (hasFreetext()) {
      <span class="tcdl-editor__stats-sep" aria-hidden="true">·</span>
      <button
        type="button"
        class="tcdl-editor__from-freetext"
        data-testid="tcdl-editor-apply-freetext"
        [disabled]="disabled()"
        (click)="applyFreetext()"
      >
        {{ 'testSteps.form.tcdlFromFreetext' | translate }}
      </button>
    }
    @if (canEncrypt()) {
      <span class="tcdl-editor__stats-sep" aria-hidden="true">·</span>
      <button
        type="button"
        class="tcdl-editor__encrypt"
        data-testid="tcdl-editor-encrypt-password"
        [attr.aria-label]="'tcdlEditor.encryptPassword' | translate"
        (click)="openEncryptDialog()"
      >
        {{ 'tcdlEditor.encryptPassword' | translate }}
      </button>
    }
  </div>
</div>

@if (encryptDialogOpen() && projectId(); as dialogProjectId) {
  <app-secret-encrypt-dialog
    [projectId]="dialogProjectId"
    (inserted)="onEncryptInserted($event)"
    (closed)="onEncryptDialogClosed()"
  />
}
